import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { RateCards, useRates } from "../components/GoldRate";
import EnquiryForm from "../components/EnquiryForm";

export default function Wholesale() {
  const [categories, setCategories] = useState([]);
  const [offices, setOffices] = useState([]);
  const { rates, loading, refresh } = useRates();

  useEffect(() => {
    api.get("/jewellery/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/offices").then((r) => setOffices(r.data.filter((o) => o.department?.toLowerCase().includes("wholesale")))).catch(() => {});
  }, []);

  return (
    <div data-testid="wholesale-page" className="pt-28 sm:pt-36">
      <SEO title="Wholesale Jewellery — Sarafa Bazar Meerut & Karol Bagh Delhi"
        description="Wholesale gold jewellery from Sarafa Bazar, Meerut and Karol Bagh, Delhi. Catalogue requests, trade accounts, and wholesale meetings for jewellers and retailers." />

      <header className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16">
        <Eyebrow>For Jewellers &amp; Retailers</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4 leading-[1.05] max-w-4xl">
          Wholesale Is the Core of <span className="italic text-[#C5A059]">Our Jewellery Business.</span>
        </h1>
        <p className="text-base text-[#94A3B8] leading-loose mt-8 max-w-2xl">
          Our flagship wholesale office operates from Sarafa Bazar, Meerut, with a second wholesale office in
          Karol Bagh, Delhi. We work directly with jewellers and retail houses on stock, purity, and weight
          requirements.
        </p>
      </header>

      <section className="bg-[#0B1320] border-y border-[#C5A059]/10 py-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Trade Desk</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 mb-10">Today’s Metal Rates</h2>
            <RateCards rates={rates} loading={loading} refresh={refresh} />
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
        <Reveal>
          <Eyebrow>Available Stock</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 mb-4">Categories &amp; Product Codes</h2>
          <p className="text-sm text-[#94A3B8] max-w-2xl leading-relaxed mb-12">
            Browse by category. Every product carries a code, metal, purity, and approximate weight range —
            use the filters on each category page, and quote the product code when you enquire.
          </p>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {categories.map((c) => (
            <Link key={c.slug} to={`/jewellery/category/${c.slug}`} data-testid={`wholesale-category-${c.slug}`}
              className="group bg-[#060A12] p-6 hover:bg-[#0B1320] transition-colors duration-500">
              <h3 className="font-serif text-xl text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-500">{c.name}</h3>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mt-2">Weight &amp; purity filters</p>
            </Link>
          ))}
        </div>
        <Reveal className="mt-10 border border-[#C5A059]/25 bg-[#0B1320] p-8">
          <p className="text-sm text-[#94A3B8] leading-relaxed">
            <span className="text-[#C5A059] uppercase tracking-[0.2em] text-[11px] font-semibold block mb-2">Credit terms</span>
            Credit terms depend on the business relationship, order profile, payment history, and internal approval.
          </p>
        </Reveal>
      </section>

      <section id="enquire" className="bg-[#FBF9F5] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <Eyebrow light>Trade Enquiries</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#1E232A] mt-3 mb-10">Request the Catalogue or a Meeting</h2>
            <div className="bg-[#060A12] p-8 sm:p-10">
              <EnquiryForm
                type="wholesale_jewellery"
                fields={[
                  { name: "business_name", label: "Business name", required: true },
                  { name: "city", label: "City", required: true },
                  { name: "interest", label: "Interested in", type: "select", required: true,
                    options: ["Request Catalogue", "Wholesale Meeting — Meerut", "Wholesale Meeting — Delhi (Karol Bagh)", "Trade Account Enquiry", "Specific Product Codes"] },
                  { name: "categories", label: "Categories of interest", placeholder: "e.g. chains, bangles, coins" },
                  { name: "weight_range", label: "Approximate weight range", placeholder: "e.g. 10–50 g pieces" },
                  { name: "purity", label: "Preferred purity", type: "select", options: ["22K (916)", "18K (750)", "24K (999)", "Silver (999)", "Mixed"] },
                ]}
              />
            </div>
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <Eyebrow light>Wholesale Offices</Eyebrow>
              <h2 className="font-serif text-3xl text-[#1E232A] mt-3 mb-10">Meerut &amp; Delhi Desks</h2>
            </Reveal>
            <div className="space-y-6">
              {offices.map((o, i) => (
                <Reveal key={o.id} delay={0.12 + i * 0.06} className="border border-[#1E232A]/15 p-8">
                  <h3 className="font-serif text-2xl text-[#1E232A]">{o.department} — {o.name}</h3>
                  <p className="text-sm text-[#1E232A]/70 mt-3 leading-relaxed">{o.address}</p>
                  {(o.phone || o.hours) && (
                    <p className="text-xs text-[#1E232A]/50 mt-3">
                      {o.phone && <>Phone: {o.phone}</>}{o.phone && o.hours && " · "}{o.hours && <>Hours: {o.hours}</>}
                    </p>
                  )}
                  {o.maps_url && (
                    <a href={o.maps_url} target="_blank" rel="noreferrer" data-testid={`wholesale-directions-${i}`}
                      className="inline-block mt-5 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#C5A059] border-b border-[#C5A059]/40 pb-1 hover:border-[#C5A059] transition-colors duration-300">
                      Get Directions →
                    </a>
                  )}
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
